import { motion } from "framer-motion";
import { Brain, Cpu, Database, Network, TrendingUp, Info, ArrowRight, ExternalLink, Copy } from "lucide-react";
import { Layout } from "@/components/Layout";
import { copyToClipboard } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { springPresets, staggerContainer, staggerItem } from "@/lib/motion";

export default function BrewPage() {
  const { t } = useLanguage();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-12">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="text-center space-y-4"
        >
          <div className="w-20 h-20 bg-primary/10 rounded-3xl flex items-center justify-center mx-auto mb-6 ring-2 ring-primary/30 shadow-xl shadow-primary/10">
            <Brain className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold text-foreground tracking-tight">{t("brew.title")}</h1>
          <p className="text-xl text-primary font-semibold">{t("brew.subtitle")}</p>
          <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto text-lg">
            {t("brew.intro")}
          </p>
        </motion.section>

        {/* Technical Specs */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.1 }}
          className="rounded-3xl border border-border/60 bg-card p-8 sm:p-10 space-y-8"
          style={{ boxShadow: "0 20px 60px oklch(0.10 0.03 235 / 0.8)" }}
        >
          <div className="flex items-center gap-3">
            <Cpu className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">{t("brew.specs")}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { label: "Modello", value: "Decoder-only" },
              { label: "Parametri", value: "150B (Blue XL)" },
              { label: "Training", value: "LinkedIn Graph" },
              { label: "Architettura", value: "Microsoft FAIT" },
            ].map((spec, i) => (
              <div 
                key={i} 
                className="group relative space-y-1 text-center sm:text-left p-3 rounded-xl hover:bg-primary/5 transition-colors cursor-pointer"
                onClick={() => copyToClipboard(`${spec.label}: ${spec.value}`)}
              >
                <p className="text-xs font-bold text-primary/60 uppercase tracking-wider">{spec.label}</p>
                <p className="text-lg font-bold text-foreground">{spec.value}</p>
                <Copy className="absolute top-2 right-2 w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </motion.section>

        {/* How it Works */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <Database className="w-6 h-6 text-primary" />
            {t("brew.howItWorks")}
          </h2>
          <div className="grid gap-4">
            {[
              { title: t("brew.step1Title"), desc: t("brew.step1Desc") },
              { title: t("brew.step2Title"), desc: t("brew.step2Desc") },
              { title: t("brew.step3Title"), desc: t("brew.step3Desc") },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="group relative p-6 rounded-2xl border border-border/40 bg-card/50 flex gap-4 hover:border-primary/40 transition-all"
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 font-bold text-primary">{i+1}</div>
                <div className="flex-1">
                  <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                  onClick={() => copyToClipboard(`${step.title}: ${step.desc}`)}
                >
                  <Copy className="w-4 h-4 text-primary" />
                </Button>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="rounded-3xl border border-primary/30 bg-primary/5 p-8 text-center space-y-4"
        >
          <h3 className="text-xl font-bold text-foreground">{t("brew.paperTitle")}</h3>
          <p className="text-muted-foreground">{t("brew.paperDesc")}</p>
          <a
            href="https://arxiv.org/abs/2306.05403"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
          >
            {t("brew.paperLink")}
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </Layout>
  );
}
